import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';

import usePayAuth from '@/hooks/mutations/pay/usePayAuth';
import {
  PaypleCpayAuthCheckRequest,
  PayplePaymentsResult,
} from '@/types/payple';
import { userInfoAtom } from '@/state/user/atom';
import { payplePayResultAtom } from '@/state/pay/atoms';
import { Nullable } from '@/types/helper';
import { SelectedPlanAtom } from '@/components/User/_components/common/ProPlanUpgradeModal/index.atom';

import useSubscriptionAndCardInfo from './queries/pay/useSubscriptionAndCardInfo';
import { PlanType } from './queries/pay/usePaymentRecord';

interface Props {
  selectedPlanId?: Nullable<number>;
  repayPlanInfo?: PlanType | undefined;
  onOpenChange: (isOpen: boolean) => void;
  isChangePlan?: boolean;
}

const usePayplePayment = ({
  selectedPlanId,
  repayPlanInfo,
  onOpenChange,
  isChangePlan = false,
}: Props) => {
  const router = useRouter();

  const userInfo = useAtomValue(userInfoAtom);
  const selectedPlanInfo = useAtomValue(SelectedPlanAtom);
  const setPayplePayResult = useSetAtom(payplePayResultAtom);

  const { data: subscriptionAndCardInfoData } = useSubscriptionAndCardInfo({
    select: (data) => data.data.data,
  });

  const getResult = (res: PayplePaymentsResult) => {
    if (res.PCD_PAY_RST === 'close') {
      router.reload();
      return;
    } else if (res.PCD_PAY_RST === 'error') {
      alert('결제중 오류가 발견되었습니다.\n다시 시도해 주세요.');
      router.reload();
      return;
    } else {
      setPayplePayResult(res);
      router.push(
        `${res.PCD_RST_URL}?planId=${
          typeof repayPlanInfo !== 'undefined'
            ? repayPlanInfo.id
            : selectedPlanId
        }&isChangePlan=${isChangePlan}`,
      );
    }
  };

  const { mutate } = usePayAuth({
    onSuccess: (data) => {
      const { PCD_AUTH_KEY, return_url } = data.data.data;

      if (!selectedPlanInfo && !repayPlanInfo) {
        alert('플랜을 선택해주세요.');
        return;
      }

      if (subscriptionAndCardInfoData?.card?.billingKey && repayPlanInfo) {
        const request = {
          PCD_PAY_TYPE: 'card',
          PCD_PAY_WORK: 'CERT',
          PCD_CARD_VER: '01',
          PCD_PAY_GOODS: `${repayPlanInfo.name} ${
            repayPlanInfo.type === 'monthly' ? '월간' : '연간'
          }`,
          PCD_PAY_TOTAL: repayPlanInfo.totalPrice.toString(),
          PCD_RST_URL: '/payments-loading',
          PCD_AUTH_KEY: PCD_AUTH_KEY,
          PCD_PAY_URL: return_url,
          PCD_PAYER_NO: userInfo?.id.toString(),
          PCD_PAYER_NAME: userInfo?.name,
          PCD_PAYER_EMAIL: userInfo?.email,
          callbackFunction: getResult,
          PCD_PAYER_ID: subscriptionAndCardInfoData.card.billingKey,
          PCD_SIMPLE_FLAG: 'Y',
        };

        (window as any).PaypleCpayAuthCheck(request);
        onOpenChange(false);
        return;
      }

      if (!selectedPlanInfo) {
        alert('선택된 Plan이 없습니다');
        return;
      }

      if (subscriptionAndCardInfoData?.card?.billingKey) {
        const request = {
          PCD_PAY_TYPE: 'card',
          PCD_PAY_WORK: 'CERT',
          PCD_CARD_VER: '01',
          PCD_PAY_GOODS: `${selectedPlanInfo.name} ${
            selectedPlanInfo.type === 'monthly' ? '월간' : '연간'
          }`,
          PCD_PAY_TOTAL: selectedPlanInfo.totalPrice.toString(),
          PCD_RST_URL: '/payments-loading',
          PCD_AUTH_KEY: PCD_AUTH_KEY,
          PCD_PAY_URL: return_url,
          PCD_PAYER_NO: userInfo?.id.toString(),
          PCD_PAYER_NAME: userInfo?.name,
          PCD_PAYER_EMAIL: userInfo?.email,
          callbackFunction: getResult,
          PCD_PAYER_ID: subscriptionAndCardInfoData.card.billingKey,
          PCD_SIMPLE_FLAG: 'Y',
        };

        (window as any).PaypleCpayAuthCheck(request);
        onOpenChange(false);
        return;
      }

      const request: PaypleCpayAuthCheckRequest = {
        PCD_PAY_TYPE: 'card',
        PCD_PAY_WORK: 'CERT',
        PCD_CARD_VER: '01',
        PCD_PAY_GOODS: `${selectedPlanInfo.name} ${
          selectedPlanInfo.type === 'monthly' ? '월간' : '연간'
        }`,
        PCD_PAY_TOTAL: selectedPlanInfo.totalPrice.toString(),
        PCD_RST_URL: '/payments-loading',
        PCD_AUTH_KEY: PCD_AUTH_KEY,
        PCD_PAY_URL: return_url,
        PCD_PAYER_NO: userInfo?.id.toString(),
        PCD_PAYER_NAME: userInfo?.name,
        PCD_PAYER_EMAIL: userInfo?.email,
        callbackFunction: getResult,
      };

      (window as any).PaypleCpayAuthCheck(request);
      onOpenChange(false);
    },
  });

  return mutate;
};

export default usePayplePayment;
